import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  FormGroup,
  IndeterminateCheckbox,
  IndeterminateCheckboxStatus,
  Button,
  ButtonType
} from 'react-magma-dom';
import { useSelector, useDispatch } from 'react-redux';

import { setSelectedStats } from 'store';

import {
  getParameters,
  setSelectedParameters,
  calculateRawDatasetSummary
} from 'store/dataset';
import Parameter from './Parameter';
import FormErrorMessage from './FormErrorMessage';
import { setCorrectVarTypes } from '../../../../../store/dataset/dataset/actions';
import { Loader } from '../../../../Loader/Loader';

const MAX_SELECTED_PARAMETERS_COUNT = 20;
const MIN_SELECTED_PARAMETERS_COUNT = 1;

const ModalContent = ({ onCloseModal, defaultTypesVariables }) => {
  const parametersFromStore = useSelector(state => getParameters(state, defaultTypesVariables));

  const dispatch = useDispatch();

  const [status, setStatus] = useState(IndeterminateCheckboxStatus.unchecked);
  const [parameters, setParameters] = useState(parametersFromStore.defaultTypes);
  const [formErrorMessage, setFormErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    const selectedParametersLength = Object.values(parameters).filter(parameter => parameter.isSelected).length;
    dispatch(setCorrectVarTypes(parametersFromStore.correctTypes));
    if (selectedParametersLength > MAX_SELECTED_PARAMETERS_COUNT) {
      setFormErrorMessage(`You can select up to ${MAX_SELECTED_PARAMETERS_COUNT} variables! Variables selected: ${selectedParametersLength}.`);
    } else {
      setFormErrorMessage('');
    }
    if (selectedParametersLength >= MIN_SELECTED_PARAMETERS_COUNT) {
      const paramsLength = Object.values(parameters).length;
      // all params are checked
      if (selectedParametersLength === paramsLength) {
        setStatus(IndeterminateCheckboxStatus.checked);
      }
      // some params are checked
      if (selectedParametersLength < paramsLength) {
        setStatus(IndeterminateCheckboxStatus.indeterminate);
      }
    } else {
      setStatus(IndeterminateCheckboxStatus.unchecked);
    }
    setIsLoading(false);
  }, [parameters]);

  const handleParameterSelectionChange = (event, name) => {
    const parameter = { [name]: { ...parameters[name], isSelected: event.target.checked } };
    setParameters({ ...parameters, ...parameter });
  };

  const handleParameterTypeChange = (name, newType) => {
    const parameter = { [name]: { ...parameters[name], targetType: newType } };
    setParameters({ ...parameters, ...parameter });
  };

  const handleUpdateIndeterminateChecked = event => {
    const updatedParameters = {};

    for (const parameter in parameters) {
      updatedParameters[parameter] = { ...parameters[parameter], isSelected: event.target.checked };
    }

    setParameters(updatedParameters);
  };

  const handleSelectButtonClick = () => {
    const newSelectedParameters = {};
    const selectedParameters = Object.values(parameters).filter(parameter => parameter.isSelected);
    if (selectedParameters.length === 0) {
      setFormErrorMessage('You need to select at least one variable!');
      return;
    }
    if (!formErrorMessage) {
      selectedParameters.forEach(parameter => {
        newSelectedParameters[parameter.name] = { ...parameter, isVisible: true };
      });

      dispatch(setSelectedParameters(newSelectedParameters));
      dispatch(calculateRawDatasetSummary());
      dispatch(setSelectedStats());

      onCloseModal();
    }
  };

  return (
    isLoading ? <Loader /> : (
      <>
        <p className="mb-5">
          You can select up to {MAX_SELECTED_PARAMETERS_COUNT} variables
        </p>
        <div className="main-modal__body main-modal__body--scrolling pt-15">
          <FormGroup errorMessage={formErrorMessage ? <FormErrorMessage message={formErrorMessage} /> : null}>
            <IndeterminateCheckbox
              status={status}
              labelText="Select all"
              hasError={false}
              labelStyle={{ fontSize: '14px' }}
              onChange={handleUpdateIndeterminateChecked}
            />
            {
              Object.values(parameters).map((parameter => (
                <Parameter
                  {...parameter}
                  key={parameter.name}
                  onSelectionChange={handleParameterSelectionChange}
                  onTypeChange={handleParameterTypeChange}
                />
              )))
            }
          </FormGroup>
        </div>
        <div className="main-modal__footer">
          <div className="text-center mb-5">
            <Button
              style={{ fontSize: '14px' }}
              type={ButtonType.button}
              onClick={handleSelectButtonClick}
            >
              Select
            </Button>
          </div>
        </div>
      </>
    )
  );
};

ModalContent.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  defaultTypesVariables: PropTypes.object.isRequired
};

export default ModalContent;
